import { Action, Middleware } from '@reduxjs/toolkit';
import { setError } from '../slices';

interface RejectedAction extends Action {
  type: string;
  payload?: string;
  error?: {
    message?: string;
    code?: string;
  };
}

const isRejectedAction = (action: unknown): action is RejectedAction => {
  return (
    typeof action === 'object' &&
    action !== null &&
    'type' in action &&
    typeof (action as RejectedAction).type === 'string'
  );
};

export const errorMiddleware: Middleware = (store) => (next) => (action: unknown) => {
  try {
    if (isRejectedAction(action) && action.type.endsWith('/rejected')) {
      // Skip automatic handling of 401 for refresh token actions
      // This allows the axios interceptor to handle token refresh
      if (action.payload === 'Unauthorized' && !action.type.includes('auth/refreshToken')) {
        store.dispatch(
          setError({
            message: 'Your session has expired.',
            severity: 'warning'
          })
        );
        return next(action);
      }

      // Only show error messages for non-refresh token actions to avoid duplicate messages
      if (!action.type.includes('auth/refreshToken')) {
        store.dispatch(
          setError({
            message: action.payload || action.error?.message || 'Unknown error',
            code: action.error?.code,
            severity: 'error'
          })
        );
      }
    }
    return next(action);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

    store.dispatch(
      setError({
        message: errorMessage,
        severity: 'error'
      })
    );
    return next(action);
  }
};
